// ── KONTEKST: ÉN kilde til «hvem, hvor, når, hvilken enhet» ────────────────────
//
// HVORFOR denne fila finnes:
// Skjemaene i COP-en lot som om systemet ikke visste noe. PERSTAT ba om
// kallesignal og enhet som FRITEKST, RFI hadde ingen avsender, og
// stridsmelding-komposisten startet med sju tomme felt — mens skallet SAMTIDIG
// viste innlogget bruker øverst til høyre, Zulu-tid i midten og egen posisjon
// som MGRS nederst til høyre. Operatøren skrev av sin egen skjerm. Under stress,
// med hansker, i mørket.
//
// Systemet vet fire ting til enhver tid:
//   HVEM   — innlogget bruker (`User` fra @skade/rbac): id, rolle, ORBAT-plassering.
//   HVOR   — egen posisjon (GPS/BFT) ELLER kartsenteret. To ULIKE ting (se under).
//   NÅR    — tida, som DTG etter Forsvarets format (`buildDtg` i kontraktene).
//   ENHET  — org-plasseringen: distrikt → område → tropp → lag.
//
// Kilden er ÉN funksjon, ikke fire ad hoc-oppslag per panel. Grunnen er den
// samme som for `@skade/contracts`: to steder som leser det samme på to måter
// begynner å svare ULIKT, og da er det ikke lenger mulig å vite hvilket svar
// som gjaldt da meldingen ble sendt.
//
// ⚠ UFRAVIKELIG: ALDRI DIKT OPP EN POSISJON.
// Kartsenteret er IKKE egen posisjon. Det er «det operatøren ser på». Å
// forhåndsutfylle et posisjonsfelt med kartsenteret og la det stå umerket er å
// sende en løgn: mottakeren leser et koordinat og tror det er målt. Derfor
// bærer posisjonen alltid sin KILDE, merkinga følger verdien hele veien ut i
// meldingsteksten, og finnes ingen av de to kildene, blir feltet TOMT.
// Et tomt felt er en operatør som må skrive; et oppdiktet felt er en enhet som
// rykker til feil sted.
//
// Ingen sanntidsklokke her: `naaMs` sendes INN (samme regel som resten av
// kodebasen — jf. `sitrep.ts`, `mappackage.ts`). Rene funksjoner = testbare.
import { forward as mgrsForward } from 'mgrs';
import { buildDtg, type ReportType, type StridsSone } from '@skade/contracts';
import type { User } from '@skade/rbac';

/** Hvor et koordinat kommer fra. To kilder som ALDRI skal forveksles. */
export type PosKilde =
  | 'gps' // målt egenposisjon (kartets geolocate / BFT)
  | 'kartsenter'; // det operatøren ser på — IKKE der operatøren er

export interface Egenposisjon {
  lon: number;
  lat: number;
  kilde: PosKilde;
}

/** En GPS-avlesning med alder, slik kartet leverer den. */
export interface GpsFix {
  lon: number;
  lat: number;
  tsMs: number;
}

/**
 * Hvor lenge en GPS-avlesning får gjelde som «hvor jeg er nå».
 *
 * 120 s er valgt fordi et kjøretøy i marsj (40 km/t) rekker ~1,3 km i løpet av
 * dem — nok til å være feil, men lite nok til at et kort GPS-utfall ikke
 * degraderer en stillestående KO til «ukjent posisjon». Går den mye høyere,
 * begynner en gammel fix å presentere seg som fersk måling; det er nettopp
 * feilen vi ikke tillater.
 */
export const GPS_FERSK_MS = 120_000;

/**
 * Velg egen posisjon ÉN gang, med kilde, av det som faktisk finnes.
 *
 * Rekkefølge: fersk GPS → kartsenter (merket) → INGENTING. Merk at en GAMMEL
 * GPS-avlesning ikke degraderes til «gps» med forbehold: den forkastes, og vi
 * faller til kartsenteret som er ærlig merket. Alternativet — å vise en fix fra
 * for en halvtime siden som egenposisjon — er den farligste av de tre.
 */
export function velgEgenposisjon(
  gps: GpsFix | null | undefined,
  kartsenter: { lon: number; lat: number } | null | undefined,
  naaMs: number,
): Egenposisjon | null {
  if (gps && gyldigKoord(gps.lon, gps.lat) && naaMs - gps.tsMs <= GPS_FERSK_MS && naaMs - gps.tsMs >= -GPS_FERSK_MS) {
    return { lon: gps.lon, lat: gps.lat, kilde: 'gps' };
  }
  if (kartsenter && gyldigKoord(kartsenter.lon, kartsenter.lat)) {
    return { lon: kartsenter.lon, lat: kartsenter.lat, kilde: 'kartsenter' };
  }
  return null;
}

/** NaN/uendelig/utenfor jorda er ikke en posisjon — det er en feil. */
function gyldigKoord(lon: number, lat: number): boolean {
  return Number.isFinite(lon) && Number.isFinite(lat) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
}

export interface KontekstInn {
  bruker: User;
  /** Egen posisjon MED kilde, eller null når ingen av kildene finnes. */
  posisjon: Egenposisjon | null;
  /** Tida sendes inn — panelene leser ikke klokka selv. */
  naaMs: number;
  /** DTG-sone. ALFA (norsk normaltid) er standard i innenlandsk stridsmelding. */
  sone?: StridsSone;
}

export interface Kontekst {
  /** Tida konteksten ble bygget på. Panelene bruker DENNE, aldri `Date.now()`. */
  naaMs: number;
  /** Kort kallesignal: «@ola.nordmann:skade» → «ola.nordmann». */
  avsender: string;
  /** Full identitet (Matrix-id) — hubben stempler den autoritativt ved sending. */
  avsenderId: string;
  /** Rolle i store bokstaver, som ellers i UI-et. */
  rolle: string;
  /** Dypeste kjente ORBAT-nivå: lag → tropp → område → distrikt. */
  enhet: string;
  /** Hele ORBAT-stien, grovest først («HV-01 / Oslofjord / Tropp 1 / Lag 2»). */
  enhetSti: string;
  /** «kallesignal / enhet» — det FRA-feltet i en stridsmelding skal inneholde. */
  avsenderOgEnhet: string;
  /** DTG: ddhhmm + sonebokstav + MND + ÅÅ. */
  dtg: string;
  /** MGRS (5 siffer), eller tom streng når posisjonen er ukjent. */
  posisjon: string;
  /** MGRS MED kildemerking — dette er verdien som skal ut i meldingstekst. */
  posisjonMerket: string;
  /** Kilden, eller null når posisjonen er ukjent. */
  posKilde: PosKilde | null;
  /** Menneskelesbar kildeforklaring til feltetiketten i UI-et. */
  posMerknad: string;
}

/** Etikett-suffiks per kilde. Merkinga følger verdien HELE veien ut. */
const POS_MERKNAD: Record<PosKilde, string> = {
  gps: 'GPS — målt egenposisjon',
  kartsenter: 'KARTSENTER — ikke målt egenposisjon',
};

/**
 * Bygg kontekstverdiene. Ren funksjon: samme inn gir samme ut, ingen DOM, ingen
 * klokke, ingen nettverk.
 */
export function byggKontekst(inn: KontekstInn): Kontekst {
  const { bruker, posisjon, naaMs } = inn;
  const avsender = kortId(bruker.id);
  const org = bruker.org ?? {};
  const nivaaer = [org.district, org.area, org.troop, org.team].filter((s): s is string => !!s && !!s.trim());
  const enhet = nivaaer.length ? nivaaer[nivaaer.length - 1] : '';
  const mgrs = posisjon ? mgrsEller(posisjon.lon, posisjon.lat) : '';

  return {
    naaMs,
    avsender,
    avsenderId: bruker.id,
    rolle: (bruker.role ?? '').toUpperCase(),
    enhet,
    enhetSti: nivaaer.join(' / '),
    avsenderOgEnhet: [avsender, enhet].filter(Boolean).join(' / '),
    dtg: buildDtg(naaMs, inn.sone ?? 'A'),
    posisjon: mgrs,
    // Uten mgrs er det ingenting å merke — tom streng, ikke «(KARTSENTER)» alene.
    posisjonMerket: mgrs && posisjon?.kilde === 'kartsenter' ? `${mgrs} (KARTSENTER)` : mgrs,
    posKilde: mgrs ? (posisjon?.kilde ?? null) : null,
    posMerknad: mgrs && posisjon ? POS_MERKNAD[posisjon.kilde] : 'ukjent — ingen GPS, ingen kartsenter',
  };
}

/** MGRS med 5-sifret presisjon, eller tom streng utenfor gyldig UTM-sone. */
function mgrsEller(lon: number, lat: number): string {
  try { return mgrsForward([lon, lat], 5); } catch { return ''; }
}

/** «@ola.nordmann:skade» → «ola.nordmann». Samme regel som i skallet. */
export function kortId(id: string): string {
  return String(id ?? '').replace(/^@/, '').replace(/:.*$/, '');
}

// ── Meta-feltene hvert skjema skal ha ferdig utfylt ──────────────────────────
// Nøklene er stabile og deles av alle rapporttyper, slik at et mottakende panel
// (og senere en eksport/ADatP-3-oversetter) kan finne avsender/DTG/posisjon
// uten å kjenne meldingstypen.

export const KONTEKST_FELT = {
  avsender: 'avsender',
  enhet: 'enhet',
  dtg: 'dtg',
  posisjon: 'posisjon',
} as const;

/** Etiketter for meta-feltene (UI + tråd-visning leser samme kilde). */
export const KONTEKST_ETIKETT: Record<string, string> = {
  avsender: 'Avsender',
  enhet: 'Enhet',
  dtg: 'DTG',
  posisjon: 'Posisjon',
};

/**
 * Meta-feltene som følger enhver innsendt rapport. Posisjonen tas MED kilde-
 * merking; er den ukjent, utelates nøkkelen helt (ingen tom nøkkel, ingen gjetning).
 */
export function kontekstFelt(k: Kontekst): Record<string, string> {
  const ut: Record<string, string> = {
    [KONTEKST_FELT.avsender]: k.avsenderOgEnhet || k.avsender,
    [KONTEKST_FELT.enhet]: k.enhet,
    [KONTEKST_FELT.dtg]: k.dtg,
  };
  if (k.posisjonMerket) ut[KONTEKST_FELT.posisjon] = k.posisjonMerket;
  for (const key of Object.keys(ut)) if (!ut[key]) delete ut[key];
  return ut;
}

// ── Forhåndsutfylling av DOKTRINÆRE felt per rapporttype ─────────────────────

/** Hvilken kontekstverdi et felt skal starte med. */
export type KontekstKilde = 'avsender' | 'avsenderOgEnhet' | 'enhet' | 'dtg' | 'posisjon';

/**
 * Kart fra rapporttype → felt som SKAL være utfylt fra kontekst.
 *
 * ⚠ Tabellen er eksplisitt og ikke navnebasert MED VILJE. En navnebasert regel
 * («felt som heter `unit` fylles med egen enhet») ville fylt SALUTE-feltet
 * «Enhet (U)» med OPERATØRENS enhet — men i SALUTE er U FIENDENS enhet. Da
 * hadde forhåndsutfyllingen produsert en observasjon av oss selv. SALUTE står
 * derfor bevisst med ingen doktrinære prefyll: alt der beskriver fienden, og det
 * er nettopp det systemet ikke vet.
 */
export const RAPPORT_FORHAANDSFELT: Record<ReportType, Array<[string, KontekstKilde]>> = {
  // FRA er avsender; HVOR er stedet (kartsenteret er der operatøren ser, derfor
  // merket); NÅR er tidspunktet hendelsen inntraff — «nå» er riktigere enn tomt,
  // og operatøren retter det når hendelsen er eldre.
  MELDING: [['fra', 'avsenderOgEnhet'], ['hvor', 'posisjon'], ['naar', 'dtg']],
  SALUTE: [], // alt beskriver FIENDEN — se kommentaren over
  CONTACT: [['callsign', 'avsender']],
  SITREP: [['unit', 'enhet']], // her ER «Enhet» egen enhet
  MEDEVAC: [['freq', 'avsender']], // L1 = eget kallesignal/frekvens
  UXO: [['contact', 'avsender']], // eget kallesignal for tilbakemelding
  CBRN: [['time', 'dtg']], // tid for hendelse
};

/**
 * Verdiene et skjema skal ÅPNE med. Tomme kontekstverdier utelates — et felt
 * skal heller stå tomt enn å bli fylt med tom streng som ser utfylt ut.
 */
export function forhaandsutfyll(type: ReportType, k: Kontekst): Record<string, string> {
  const ut: Record<string, string> = {};
  for (const [felt, kilde] of RAPPORT_FORHAANDSFELT[type] ?? []) {
    const v = kontekstVerdi(k, kilde);
    if (v) ut[felt] = v;
  }
  return ut;
}

/** Slå opp én kontekstverdi. Posisjon leveres alltid MERKET. */
export function kontekstVerdi(k: Kontekst, kilde: KontekstKilde): string {
  switch (kilde) {
    case 'avsender': return k.avsender;
    case 'avsenderOgEnhet': return k.avsenderOgEnhet;
    case 'enhet': return k.enhet;
    case 'dtg': return k.dtg;
    case 'posisjon': return k.posisjonMerket;
  }
}
